import { Loader2 } from 'lucide-react'
import { useCallback, useRef } from 'react'
import { cn } from '@/lib/utils'

interface InfiniteScrollSentinelProps {
  hasNextPage: boolean
  isFetching: boolean
  onLoadMore: () => void
  className?: string
  /** Distance from the scroll viewport at which to start loading. Default 200px. */
  rootMargin?: string
}

/**
 * A zero-height marker that auto-loads the next page when scrolled into view.
 * Renders nothing once there are no more pages. Shared by every infinite list
 * (notifications, tasks, …) so the IntersectionObserver wiring lives once.
 */
export function InfiniteScrollSentinel({
  hasNextPage,
  isFetching,
  onLoadMore,
  className,
  rootMargin = '200px',
}: InfiniteScrollSentinelProps) {
  const observer = useRef<IntersectionObserver | null>(null)

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) observer.current.disconnect()
      if (!node || !hasNextPage) return
      observer.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isFetching) onLoadMore()
        },
        { rootMargin },
      )
      observer.current.observe(node)
    },
    [hasNextPage, isFetching, onLoadMore, rootMargin],
  )

  if (!hasNextPage) return null

  return (
    <div
      ref={sentinelRef}
      data-testid="infinite-scroll-sentinel"
      className={cn('flex h-12 items-center justify-center text-muted-foreground', className)}
    >
      {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
    </div>
  )
}
